/**
 * D454 step 3: arranging the land.
 *
 * Links are the theme's own pairs (`content.arrange.links`): a crop next to the animals it feeds,
 * bees next to a flowering crop. A link is made when both producers are open this year and their
 * plots sit side by side, sharing an edge at least a tile long: across one lane on the map, never
 * corner to corner. Each link pays the producer it goes to `arrange.link` more value.
 *
 * The hands' building is the yard. Its reach is a ring `arrange.yardReach` tiles from the yard's
 * middle; every open plot on the world the ring touches earns `arrange.yard` more. A producer's
 * links and the yard add up to at most `arrange.cap`. Links and the yard pay from the same
 * generation moving opens, so the first generation's opening is as it was.
 *
 * From the generation `arrange.moveFrom` names, an open plot may be moved, free: dropped on
 * another plot, the two swap places when each place is at least `arrange.fit` of the other's
 * area; dropped on the plot of a producer not open this year, they swap whatever the size, since
 * that ground is free; dropped on free ground (a filler tile, say), it keeps its size and must
 * lie wholly on the land, clear of the yard, the pond, the first row of festival marks and every
 * other plot. A plot takes the shape of the place it moves to. Fillers are worked out again.
 *
 * Worked out from the layout and the producers open, so a bonus follows a plot wherever it sits.
 * A world that never moves a plot keeps what its seed laid (D218). Pure.
 */
import type { IdleContent, IdleLinkT } from '@hedgerow/data';
import { idleLayout, idleLoneTiles, idleOnWorld } from './layout.ts';
import type { IdleLayout, IdleLayoutRect, IdleWorldState } from './state.ts';
import type { IdleRefusal, IdleResult } from './world.ts';

/** A link in force: the pair from the content and what it adds to `to`, as a fraction. */
export interface IdleActiveLink extends IdleLinkT {
  bonus: number;
}

/** A producer's bonus from where it sits: the total after the cap, and where it comes from. */
export interface IdleLinkBonus {
  bonus: number;
  /** The producers whose links feed it, in content order. */
  from: string[];
  /** Inside the hands' yard. */
  yard: boolean;
}

/** Whether two rectangles share an edge at least a tile long (a corner is not enough). */
export function idleRectsTouch(a: IdleLayoutRect, b: IdleLayoutRect): boolean {
  const side = (a.x + a.w === b.x || b.x + b.w === a.x) && a.y < b.y + b.h && b.y < a.y + a.h;
  const end = (a.y + a.h === b.y || b.y + b.h === a.y) && a.x < b.x + b.w && b.x < a.x + a.w;
  return side || end;
}

/** Each producer's plot, on the world or on a piece of land beside it (D301). */
export function idlePlotsOf(layout: IdleLayout): Record<string, IdleLayoutRect> {
  const out: Record<string, IdleLayoutRect> = {};
  for (const land of Object.values(layout.lands ?? {})) Object.assign(out, land.plots);
  return Object.assign(out, layout.plots);
}

/** The yard's middle, in tiles: where the hands' reach is measured from. */
export function idleYardMiddle(layout: IdleLayout): { x: number; y: number } {
  return { x: layout.yard.x + layout.yard.w / 2, y: layout.yard.y + layout.yard.h / 2 };
}

/** Whether the hands' reach touches a plot: its nearest point within `reach` of the yard's middle. */
export function idleInYard(layout: IdleLayout, r: IdleLayoutRect, reach: number): boolean {
  const m = idleYardMiddle(layout);
  const dx = Math.max(r.x - m.x, 0, m.x - (r.x + r.w));
  const dy = Math.max(r.y - m.y, 0, m.y - (r.y + r.h));
  return dx * dx + dy * dy <= reach * reach;
}

interface Geometry {
  /** The content's pairs whose plots touch, open or not. */
  pairs: IdleLinkT[];
  /** Producers on the world whose plot the hands' reach touches. */
  yard: Set<string>;
}

/** By layout object, so the advance stays cheap. */
const geometries = new WeakMap<object, WeakMap<IdleContent, Geometry>>();

function geometryOf(layout: IdleLayout, content: IdleContent): Geometry {
  let byContent = geometries.get(layout);
  const hit = byContent?.get(content);
  if (hit) return hit;
  const plots = idlePlotsOf(layout);
  const pairs = content.arrange.links.filter((l) => {
    const a = plots[l.from];
    const b = plots[l.to];
    return a !== undefined && b !== undefined && idleRectsTouch(a, b);
  });
  const yard = new Set<string>();
  const reach = content.arrange.yardReach;
  if (reach > 0)
    for (const [id, r] of Object.entries(layout.plots))
      if (idleInYard(layout, r, reach)) yard.add(id);
  const g = { pairs, yard };
  if (!byContent) geometries.set(layout, (byContent = new WeakMap()));
  byContent.set(content, g);
  return g;
}

/**
 * The layout the links read, as `idleLayout` gives it (relaid, with its lands), cached by the
 * stored layout (or the content, for a world with none) so the advance stays cheap.
 */
const read = new WeakMap<object, WeakMap<IdleContent, IdleLayout>>();

function layoutOf(state: IdleWorldState, content: IdleContent): IdleLayout {
  const k: object = state.layout ?? content;
  let byContent = read.get(k);
  let l = byContent?.get(content);
  if (l) return l;
  l = idleLayout(state, content);
  if (!byContent) read.set(k, (byContent = new WeakMap()));
  byContent.set(content, l);
  return l;
}

/**
 * The links in force on `layout` (the world's own when absent), in content order: both
 * producers open this year and their plots side by side.
 */
export function idleLinks(
  state: IdleWorldState,
  content: IdleContent,
  layout: IdleLayout = layoutOf(state, content),
): IdleActiveLink[] {
  const a = content.arrange;
  if (a.link <= 0 || a.cap <= 0 || a.links.length === 0) return [];
  if (!idleArrangeOpen(state, content)) return [];
  const open = state.year.producers;
  return geometryOf(layout, content)
    .pairs.filter((l) => open[l.from] && open[l.to])
    .map((l) => ({ ...l, bonus: Math.min(a.link, a.cap) }));
}

/** Every open producer's bonus from where it sits, by id, capped; one with none is left out. */
export function idleLinkBonuses(
  state: IdleWorldState,
  content: IdleContent,
  layout: IdleLayout = layoutOf(state, content),
): Record<string, IdleLinkBonus> {
  const a = content.arrange;
  const out: Record<string, IdleLinkBonus> = {};
  if (a.cap <= 0 || !idleArrangeOpen(state, content)) return out;
  const raw: Record<string, number> = {};
  for (const l of idleLinks(state, content, layout)) {
    const b = (out[l.to] ??= { bonus: 0, from: [], yard: false });
    b.from.push(l.from);
    raw[l.to] = (raw[l.to] ?? 0) + l.bonus;
  }
  if (a.yard > 0)
    for (const id of geometryOf(layout, content).yard) {
      if (!state.year.producers[id]) continue;
      const b = (out[id] ??= { bonus: 0, from: [], yard: false });
      b.yard = true;
      raw[id] = (raw[id] ?? 0) + a.yard;
    }
  for (const [id, b] of Object.entries(out)) b.bonus = Math.min(a.cap, raw[id] ?? 0);
  return out;
}

/** What a new layout does: links made and broken, and each producer whose bonus changes. */
export interface IdleLinkChanges {
  gained: IdleActiveLink[];
  lost: IdleActiveLink[];
  /** Producers whose bonus changes, in content order, with the fraction before and after. */
  bonuses: Array<{ producer: string; before: number; after: number }>;
}

/** What changing the world's layout to `after` does to its links and bonuses, for the dock. */
export function idleLinkChanges(
  state: IdleWorldState,
  content: IdleContent,
  after: IdleLayout,
): IdleLinkChanges {
  const key = (l: IdleLinkT) => `${l.from}>${l.to}`;
  const before = idleLinks(state, content);
  const now = idleLinks(state, content, after);
  const had = new Set(before.map(key));
  const has = new Set(now.map(key));
  const b0 = idleLinkBonuses(state, content);
  const b1 = idleLinkBonuses(state, content, after);
  const bonuses = content.producers
    .map((p) => ({ producer: p.id, before: b0[p.id]?.bonus ?? 0, after: b1[p.id]?.bonus ?? 0 }))
    .filter((c) => Math.abs(c.after - c.before) > 1e-12);
  return {
    gained: now.filter((l) => !had.has(key(l))),
    lost: before.filter((l) => !has.has(key(l))),
    bonuses,
  };
}

/** Where a plot is dropped: on another producer's plot, or with its top-left tile at `x,y`. */
export type IdleMoveTarget = { with: string } | { x: number; y: number };

/** A move worked out: the layout it gives and what it changes. */
export interface IdleMovePlan {
  layout: IdleLayout;
  /** The producer it swaps with, if any. */
  with?: string;
  changes: IdleLinkChanges;
}

export type IdleMoveCheck =
  ({ ok: true } & IdleMovePlan) | { ok: false; reason: IdleRefusal; with?: string };

const area = (r: IdleLayoutRect) => r.w * r.h;

/** Whether plots may be moved yet: from the generation `arrange.moveFrom` names. */
export function idleArrangeOpen(state: IdleWorldState, content: IdleContent): boolean {
  return (state.permanent.lineage?.generation ?? 1) >= content.arrange.moveFrom;
}

function withPlots(layout: IdleLayout, plots: Record<string, IdleLayoutRect>): IdleLayout {
  const { fillers: _, ...rest } = { ...layout, plots };
  const lone = idleLoneTiles(rest);
  return lone.length > 0 ? { ...rest, fillers: lone } : rest;
}

/**
 * What dropping `producer`'s plot on `target` would do, or why it cannot: for the dock while
 * a plot is held, and the same check the move makes.
 */
export function idleMoveCheck(
  state: IdleWorldState,
  content: IdleContent,
  producer: string,
  target: IdleMoveTarget,
): IdleMoveCheck {
  if (!idleArrangeOpen(state, content)) return { ok: false, reason: 'too_early' };
  const layout = layoutOf(state, content);
  const from = layout.plots[producer];
  if (!from || !state.year.producers[producer]) return { ok: false, reason: 'no_plot' };
  let plots: Record<string, IdleLayoutRect>;
  let other: string | undefined;
  if ('with' in target) {
    other = target.with;
    const to = layout.plots[other];
    if (!to || other === producer) return { ok: false, reason: 'no_plot' };
    // A producer not open this year has no field yet: its ground is free.
    if (state.year.producers[other]) {
      const fit = content.arrange.fit;
      if (area(to) < fit * area(from)) return { ok: false, reason: 'too_big', with: other };
      if (area(from) < fit * area(to)) return { ok: false, reason: 'other_too_big', with: other };
    }
    plots = { ...layout.plots, [producer]: { ...to }, [other]: { ...from } };
  } else {
    const to = { x: target.x, y: target.y, w: from.w, h: from.h };
    const onWorld = idleOnWorld(layout);
    const taken = new Set<string>();
    const marks: IdleLayoutRect[] = [];
    for (let i = 0; i < layout.marks.perRow; i++)
      marks.push({ x: layout.marks.x + i * layout.marks.dx, y: layout.marks.y, w: 2, h: 2 });
    const others = Object.entries(layout.plots)
      .filter(([id]) => id !== producer)
      .map(([, r]) => r);
    for (const r of [layout.yard, ...others, ...(layout.pond ? [layout.pond] : []), ...marks])
      for (let ty = r.y; ty < r.y + r.h; ty++)
        for (let tx = r.x; tx < r.x + r.w; tx++) taken.add(`${tx},${ty}`);
    for (let ty = to.y; ty < to.y + to.h; ty++)
      for (let tx = to.x; tx < to.x + to.w; tx++)
        if (!onWorld(tx, ty) || taken.has(`${tx},${ty}`)) return { ok: false, reason: 'bad_spot' };
    plots = { ...layout.plots, [producer]: to };
  }
  const moved = withPlots(layout, plots);
  const changes = idleLinkChanges(state, content, moved);
  return other === undefined
    ? { ok: true, layout: moved, changes }
    : { ok: true, layout: moved, with: other, changes };
}

/** D454 step 3: move a producer's plot, free. Refused as `idleMoveCheck` says. */
export function moveIdlePlot(
  state: IdleWorldState,
  content: IdleContent,
  producer: string,
  target: IdleMoveTarget,
): IdleResult {
  const c = idleMoveCheck(state, content, producer, target);
  if (!c.ok) return { ok: false, reason: c.reason };
  return { ok: true, state: { ...state, layout: c.layout } };
}
